// Company Master Hierarchy Page
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Building2, ChevronRight, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { Modal, Field, Spinner, Table } from '../components/common';
import { fmt } from '../utils/helpers';
import toast from 'react-hot-toast';

const companyApi = {
  divisions: p   => api.get('/masters/divisions', { params: p }).then(r => r.data),
  departments: p => api.get('/masters/departments', { params: p }).then(r => r.data),
  zones:  ()     => api.get('/masters/zones').then(r => r.data),
  units:  ()     => api.get('/masters/units').then(r => r.data),
  createZone:  d => api.post('/masters/zones', d).then(r => r.data),
  updateZone:  (id,d) => api.put(`/masters/zones/${id}`, d).then(r => r.data),
  createUnit:  d => api.post('/masters/units', d).then(r => r.data),
  updateUnit:  (id,d) => api.put(`/masters/units/${id}`, d).then(r => r.data),
};

export function CompanyHierarchy() {
  const [tab, setTab]       = useState('divisions');
  const [zones, setZones]   = useState([]);
  const [units, setUnits]   = useState([]);
  const [showForm, setShowForm] = useState(null); // 'zone' | 'unit'
  const [editRow, setEditRow]   = useState(null);

  useEffect(() => {
    companyApi.zones().then(r => setZones(r.data || []));
    companyApi.units().then(r => setUnits(r.data || []));
  }, []);

  const reload = () => {
    companyApi.zones().then(r => setZones(r.data || []));
    companyApi.units().then(r => setUnits(r.data || []));
  };

  const zoneCols = [
    { key: 'name', title: 'Zone Name',  render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'code', title: 'Code',       render: v => <span style={{ fontFamily:'monospace', fontSize:'var(--fs-xs)', background:'var(--bg)', padding:'2px 6px', borderRadius:4 }}>{v}</span> },
    { key: 'id', title: '', align: 'right', render: (_, r) => <button className="btn btn-ghost btn-sm" onClick={() => { setEditRow(r); setShowForm('zone'); }}>Edit</button> },
  ];

  const unitCols = [
    { key: 'name',      title: 'Unit Name', render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'code',      title: 'Code',      render: v => <span style={{ fontFamily:'monospace', fontSize:'var(--fs-xs)', background:'var(--bg)', padding:'2px 6px', borderRadius:4 }}>{v}</span> },
    { key: 'zone_name', title: 'Zone',      render: v => <span style={{ fontSize:'var(--fs-xs)', color:'var(--text-secondary)' }}>{v || '—'}</span> },
    { key: 'id', title: '', align: 'right', render: (_, r) => <button className="btn btn-ghost btn-sm" onClick={() => { setEditRow(r); setShowForm('unit'); }}>Edit</button> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Company Hierarchy</h1><p className="page-sub">Company → Zone → Unit · Division → Department organisation</p></div>
        <div className="page-actions">
          <button className="btn btn-ghost btn-sm" onClick={reload}><RefreshCw size={13}/></button>
          {tab === 'zones' && <button className="btn btn-primary" onClick={() => { setEditRow(null); setShowForm('zone'); }}><Plus size={14}/> Add Zone</button>}
          {tab === 'units' && <button className="btn btn-primary" onClick={() => { setEditRow(null); setShowForm('unit'); }}><Plus size={14}/> Add Unit</button>}
        </div>
      </div>

      {/* Hierarchy diagram */}
      <div style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'16px 20px', marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:'var(--fs-sm)', color:'var(--text-secondary)', flexWrap:'wrap' }}>
          {[['Company','#1A5FA8'],['Zone','#0F8A6E'],['Unit','#B56A00'],['Project','#5A3DBB']].map(([l,c],i) => (
            <React.Fragment key={l}>
              {i > 0 && <ChevronRight size={14} color="var(--text-muted)"/>}
              <span style={{ background:c+'18', color:c, padding:'4px 10px', borderRadius:20, fontWeight:500, fontSize:'var(--fs-xs)' }}>{l}</span>
            </React.Fragment>
          ))}
          <span style={{ marginLeft:20, color:'var(--text-muted)', fontSize:'var(--fs-xs)' }}>AND</span>
          {[['Division','#1A5FA8'],['Department','#0F8A6E']].map(([l,c],i) => (
            <React.Fragment key={l}>
              {i > 0 && <ChevronRight size={14} color="var(--text-muted)"/>}
              <span style={{ background:c+'18', color:c, padding:'4px 10px', borderRadius:20, fontWeight:500, fontSize:'var(--fs-xs)' }}>{l}</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="tabs">
        {[{k:'divisions',l:'Divisions'},{k:'departments',l:'Departments'},{k:'zones',l:'Zones'},{k:'units',l:'Units'}].map(t => (
          <button key={t.k} className={`tab-btn${tab===t.k?' active':''}`} onClick={() => setTab(t.k)}>{t.l}</button>
        ))}
      </div>

      {tab === 'zones'       && <div className="card"><Table columns={zoneCols} data={zones} emptyText="No zones yet"/></div>}
      {tab === 'units'       && <div className="card"><Table columns={unitCols} data={units} emptyText="No units yet"/></div>}
      {tab === 'divisions'   && <DivisionsInline />}
      {tab === 'departments' && <DepartmentsInline />}

      {/* Zone Modal */}
      <Modal open={showForm==='zone'} onClose={() => setShowForm(null)} title={editRow ? 'Edit Zone' : 'Add Zone'} size="sm"
        footer={<ZoneUnitFooter type="zone" editData={editRow} onClose={() => setShowForm(null)} onSuccess={() => { setShowForm(null); reload(); }} />}>
        <ZoneUnitForm type="zone" editData={editRow} />
      </Modal>

      {/* Unit Modal */}
      <Modal open={showForm==='unit'} onClose={() => setShowForm(null)} title={editRow ? 'Edit Unit' : 'Add Unit'} size="sm"
        footer={<ZoneUnitFooter type="unit" editData={editRow} onClose={() => setShowForm(null)} onSuccess={() => { setShowForm(null); reload(); }} />}>
        <ZoneUnitForm type="unit" editData={editRow} zones={zones} />
      </Modal>
    </div>
  );
}

function ZoneUnitForm({ type, editData, zones = [] }) {
  return null; // Controlled externally by parent state
}

function ZoneUnitFooter({ type, editData, onClose, onSuccess }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [zones, setZones]     = useState([]);

  useEffect(() => {
    setForm(editData || {});
    if (type === 'unit') companyApi.zones().then(r => setZones(r.data || []));
  }, [editData, type]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      if (type === 'zone') {
        editData ? await companyApi.updateZone(editData.id, form) : await companyApi.createZone(form);
      } else {
        editData ? await companyApi.updateUnit(editData.id, form) : await companyApi.createUnit(form);
      }
      toast.success('Saved');
      onSuccess();
    } catch {} finally { setLoading(false); }
  };

  return (
    <>
      {/* Inline form rendered in footer slot */}
      <div style={{ width:'100%' }}>
        <div className="form-group" style={{ marginBottom:10 }}>
          <label className="form-label">Name <span className="req">*</span></label>
          <input className="form-control" value={form.name||''} onChange={set('name')} />
        </div>
        <div className="form-group" style={{ marginBottom:10 }}>
          <label className="form-label">Code</label>
          <input className="form-control" value={form.code||''} onChange={set('code')} style={{ textTransform: form.code ? 'uppercase' : 'none' }} placeholder={editData ? '' : 'Optional'} disabled={!!editData} />
        </div>
        {type === 'unit' && (
          <div className="form-group" style={{ marginBottom:10 }}>
            <label className="form-label">Zone</label>
            <select className="form-control" value={form.zone_id||''} onChange={set('zone_id')}>
              <option value="">No Zone</option>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <Spinner size="sm"/> : 'Save'}</button>
        </div>
      </div>
    </>
  );
}

function DivisionsInline() {
  const { Divisions } = require('./Masters');
  return <Divisions />;
}
function DepartmentsInline() {
  const { Departments } = require('./Masters');
  return <Departments />;
}

export default CompanyHierarchy;


// ═══════════════════════════════════════════════════════════
// Forgot Password Page (public route)
// ═══════════════════════════════════════════════════════════

import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export function ForgotPassword() {
  const navigate = useNavigate();
  const [step,     setStep]    = useState(1); // 1=email, 2=otp+newpwd
  const [email,    setEmail]   = useState('');
  const [otp,      setOtp]     = useState('');
  const [newPwd,   setNewPwd]  = useState('');
  const [loading,  setLoading] = useState(false);
  const [msg,      setMsg]     = useState('');
  const [error,    setError]   = useState('');

  const BASE = process.env.REACT_APP_API_URL || '/api/v1';

  const sendOTP = async e => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${BASE}/auth/forgot-password`, { email });
      setMsg('A 6-digit reset code has been sent to your email.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Request failed');
    } finally { setLoading(false); }
  };

  const resetPwd = async e => {
    e.preventDefault();
    if (newPwd.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${BASE}/auth/reset-password`, { email, otp, new_password: newPwd });
      toast.success('Password reset successfully! Please login.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#0F2D54,#1A5FA8)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 20px 60px rgba(0,0,0,.25)', width:'100%', maxWidth:400, padding:32 }}>
        <h2 style={{ fontSize:20, fontWeight:700, marginBottom:6 }}>{step === 1 ? 'Reset Password' : 'Enter Reset Code'}</h2>
        <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:24 }}>
          {step === 1 ? 'Enter your email and we\'ll send a reset code.' : msg}
        </p>
        {error && <div className="alert alert-danger" style={{ marginBottom:16 }}><span>{error}</span></div>}
        {step === 1 ? (
          <form onSubmit={sendOTP}>
            <div className="form-group"><label className="form-label">Email <span className="req">*</span></label><input className="form-control" type="email" value={email} onChange={e=>setEmail(e.target.value)} required /></div>
            <button type="submit" className="btn btn-primary w-full" disabled={loading}>{loading ? <Spinner size="sm"/> : 'Send Reset Code'}</button>
          </form>
        ) : (
          <form onSubmit={resetPwd}>
            <div className="form-group"><label className="form-label">6-Digit Code <span className="req">*</span></label><input className="form-control" value={otp} onChange={e=>setOtp(e.target.value)} placeholder="Enter code from email" maxLength={6} style={{ letterSpacing:6, fontSize:18, textAlign:'center', fontFamily:'monospace' }} required /></div>
            <div className="form-group"><label className="form-label">New Password <span className="req">*</span></label><input className="form-control" type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} required /></div>
            <button type="submit" className="btn btn-primary w-full" disabled={loading}>{loading ? <Spinner size="sm"/> : 'Reset Password'}</button>
          </form>
        )}
        <button className="btn btn-ghost w-full" style={{ marginTop:10 }} onClick={() => navigate('/login')}>Back to Login</button>
      </div>
    </div>
  );
}
